import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { teamApi, PromptConfig } from '@/services/api';

const DEFAULT_SYSTEM_PROMPT = `You are {bot_persona}, a support assistant for {team_name}. Follow these rules strictly.

Rules:
1. Format for Google Chat: use single asterisks for bold (*text*), not double (**text**).
2. Never repeat, paraphrase, or quote the user's message.
3. Include a root cause only if you can confirm it from logs, config, or error output — not from inference or educated guessing. An error message alone is not enough — it only tells you the symptom, not the cause. If you're not certain, omit it entirely.
4. No code examples, sample configs, numbered lists, or documentation links unless the user explicitly asks.
5. Keep responses to 2–4 sentences max. This means the entire response, not per section. Be direct, no filler.
6. Determine scope using keyword matching with the responsibility matrix:
   - If any expert or team-member keywords match → in scope.
   - If none match → possibly out of scope.
7. Never invent teams, roles, or experts. Only use the responsibility matrix.
8. End every response with:
   *_I may miss small details; feel free to confirm with a team member if needed._*

Critical reminders (enforce strictly):
- Do not use double asterisks. Google Chat bold is *text*, not **text**.
- 2–4 sentences means the entire response, not per section.
- A root cause requires actual evidence in the message (logs, config, exact values). An error message alone is not enough.

Format:
Start with a brief diagnosis (1 sentence identifying what's wrong), then the action to fix it. Use only the sections that apply:

🔍 *Issue:* (1 sentence — what's wrong, based on the error/logs provided)
💡 *Next step:* (the concrete action to resolve it)`;

const DEFAULT_OUT_OF_SCOPE = `💡 *SUGGESTION:*
This topic is not in our scope, please contact {responsible_team} to further investigate.

ℹ️ *NOTICE:*
I'm an AI, and I've done my best to provide a helpful response. However, if you need further clarification or assistance, please don't hesitate to reach out to a team member, who can provide more detailed information and support.`;

const DEFAULT_RESPONSE_OVERRIDE = `1. FORMATTING: Use SINGLE asterisks for bold (*text*), never double (**text**). This is Google Chat, not Markdown.
2. BREVITY: Keep your answer to 2-4 sentences per section. Do not over-explain. Only state facts you are certain about.
3. NO GUESSING: If you are not certain about a root cause, do not include it. Only provide confirmed information.`;

const DEFAULT_NOTICE = "I'm an AI, and I've done my best to provide a helpful response. However, if you need further clarification or assistance, please don't hesitate to reach out to a team member, who can provide more detailed information and support.";

interface PromptingSubTabProps {
  teamId: string;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

export default function PromptingSubTab({ teamId, onNotify }: PromptingSubTabProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<PromptConfig>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const { data: promptConfig } = useQuery({
    queryKey: ['prompt-config', teamId],
    queryFn: () => teamApi.getPromptConfig(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  useEffect(() => {
    if (promptConfig) {
      setFormData(promptConfig);
      setHasChanges(false);
    }
  }, [promptConfig]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PromptConfig>) => teamApi.updatePromptConfig(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt-config', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team-prompt-config', teamId] });
      onNotify('Prompt configuration saved!', 'success');
      setHasChanges(false);
    },
    onError: (error: Error) => {
      onNotify(`Failed to save: ${getErrorMessage(error)}`, 'error');
    },
  });

  const handleChange = (field: keyof PromptConfig, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleReset = (field: keyof PromptConfig) => {
    const defaults: Record<string, unknown> = {
      system_prompt: DEFAULT_SYSTEM_PROMPT,
      out_of_scope_response: DEFAULT_OUT_OF_SCOPE,
      notice_text: DEFAULT_NOTICE,
      response_override: DEFAULT_RESPONSE_OVERRIDE,
      team_display_name: 'Support Team',
      bot_persona: 'AI Wingman',
    };
    if (defaults[field]) {
      handleChange(field, defaults[field]);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Typography variant="h6">Prompting Configuration</Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => updateMutation.mutate(formData)}
            disabled={!hasChanges || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Prompts'}
          </Button>
        </Box>

        {hasChanges && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You have unsaved prompt changes.
          </Alert>
        )}

        {/* Bot Identity */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Bot Identity
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure how the bot identifies itself and your team in responses.
        </Typography>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Team Display Name"
              fullWidth
              value={formData.team_display_name || ''}
              onChange={(e) => handleChange('team_display_name', e.target.value)}
              helperText="Used in responses as {team_name}. E.g., 'IONOS SDK Team'"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Bot Persona"
              fullWidth
              value={formData.bot_persona || ''}
              onChange={(e) => handleChange('bot_persona', e.target.value)}
              helperText="Bot's name in responses as {bot_persona}. E.g., 'AI Wingman'"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* System Prompt */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              System Prompt
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The main instruction template for the AI. Use placeholders: {'{bot_persona}'}, {'{team_name}'}.
            </Typography>
          </Box>
          <Button size="small" startIcon={<ResetIcon />} onClick={() => handleReset('system_prompt')}>
            Reset
          </Button>
        </Box>
        <TextField
          multiline
          rows={12}
          fullWidth
          value={formData.system_prompt || ''}
          onChange={(e) => handleChange('system_prompt', e.target.value)}
          sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace' }, mb: 3 }}
        />

        <Divider sx={{ my: 3 }} />

        {/* Out of Scope Response */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Out of Scope Response
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Template when a topic is outside your team's responsibility. Use {'{responsible_team}'}.
            </Typography>
          </Box>
          <Button size="small" startIcon={<ResetIcon />} onClick={() => handleReset('out_of_scope_response')}>
            Reset
          </Button>
        </Box>
        <TextField
          multiline
          rows={5}
          fullWidth
          value={formData.out_of_scope_response || ''}
          onChange={(e) => handleChange('out_of_scope_response', e.target.value)}
          sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace' }, mb: 3 }}
        />

        <Divider sx={{ my: 3 }} />

        {/* Notice / Disclaimer */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Notice / Disclaimer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Standard disclaimer added to AI responses.
            </Typography>
          </Box>
          <Button size="small" startIcon={<ResetIcon />} onClick={() => handleReset('notice_text')}>
            Reset
          </Button>
        </Box>
        <TextField
          multiline
          rows={3}
          fullWidth
          value={formData.notice_text || ''}
          onChange={(e) => handleChange('notice_text', e.target.value)}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ my: 3 }} />

        {/* Response Override */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Response Override
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Final instruction injected before the user's message. Controls formatting, brevity, and behavior rules.
            </Typography>
          </Box>
          <Button size="small" startIcon={<ResetIcon />} onClick={() => handleReset('response_override')}>
            Reset
          </Button>
        </Box>
        <TextField
          multiline
          rows={5}
          fullWidth
          value={formData.response_override || ''}
          onChange={(e) => handleChange('response_override', e.target.value)}
          placeholder={DEFAULT_RESPONSE_OVERRIDE}
          sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace' } }}
        />
      </CardContent>
    </Card>
  );
}
